import type { FamilyChild } from '@oefy/contracts';
import { ArrowRight, Lock, RotateCcw, UserRound } from 'lucide-react';
import { Link, Navigate } from 'react-router';
import { useSession } from '../auth/session';
import { ChildAvatar } from '../ui/avatar';
import { Button } from '../ui/button';
import { Mascot } from '../ui/mascot';
import { StatusScreen } from '../ui/status-screen';
import { ChildNotice } from './child-notice';
import { ChildShell } from './child-shell';
import { copy } from './copy';
import { useFamily } from './queries';

export function HomePage() {
  const session = useSession();
  const family = useFamily();

  if (session.data?.role === 'staff') {
    return <Navigate to="/beheer" replace />;
  }
  if (family.isPending) {
    return (
      <ChildShell wormMessage={copy.worm.pickChild} wormPose="wave">
        <section className="mx-auto mt-8 max-w-2xl text-center">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">{copy.whoIsPracticing}</h1>
        </section>
        <ul
          role="status"
          aria-label="Bezig met laden"
          className="mx-auto mt-12 grid max-w-4xl grid-cols-2 gap-4 sm:flex sm:justify-center sm:gap-6"
        >
          {[0, 1].map((index) => (
            <li
              key={index}
              aria-hidden="true"
              className="flex flex-col items-center gap-3 rounded-4xl bg-white/70 px-4 py-7 ring-1 ring-white sm:w-52"
            >
              <span className="size-28 rounded-full bg-amber-100 motion-safe:animate-pulse" />
              <span className="h-6 w-20 rounded-full bg-amber-100 motion-safe:animate-pulse" />
              <span className="h-5 w-16 rounded-full bg-amber-100/70 motion-safe:animate-pulse" />
            </li>
          ))}
        </ul>
      </ChildShell>
    );
  }
  if (!family.data) {
    return (
      <StatusScreen
        tone="warning"
        icon={<RotateCcw aria-hidden="true" />}
        title="Dat ging even mis"
        actions={<Button onClick={() => void family.refetch()}>Opnieuw proberen</Button>}
      >
        {copy.somethingWrong}
      </StatusScreen>
    );
  }
  if (family.data.children.length === 0) {
    return <Navigate to="/welkom" replace />;
  }

  return (
    <ChildShell wormMessage={copy.worm.pickChild} wormPose="wave" aside={<ParentsLink />}>
      <section className="mx-auto mt-8 max-w-2xl text-center">
        <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">{copy.whoIsPracticing}</h1>
        <p className="mt-3 text-xl text-slate-600">{copy.tapYourName}</p>
      </section>

      {!family.data.hasAccess ? (
        <ChildNotice className="mt-8" icon={<Lock aria-hidden="true" />}>
          {copy.noAccess}
        </ChildNotice>
      ) : null}
      <SchoolYearBanner kids={family.data.children.filter((child) => child.groupCheckDue)} />

      <ul className="mx-auto mt-12 grid max-w-4xl grid-cols-2 gap-4 sm:flex sm:flex-wrap sm:justify-center sm:gap-6">
        {family.data.children.map((child) => (
          <li key={child.id}>
            <Link
              to={`/oefenen/${child.id}`}
              className="group flex h-full flex-col items-center gap-3 rounded-4xl bg-white px-4 py-7 shadow-warm ring-1 ring-slate-200 transition hover:-translate-y-1 hover:shadow-raised sm:w-52"
            >
              <span className="relative">
                <ChildAvatar
                  avatar={child.avatar}
                  color={child.avatarColor}
                  photoUrl={child.photoUrl}
                  size="2xl"
                  className="shadow-md ring-4 ring-white transition duration-300 group-hover:scale-105 group-hover:-rotate-3"
                />
                {child.groups.length === 0 ? (
                  <span className="absolute -right-1 bottom-0 flex size-9 items-center justify-center rounded-full bg-amber-400 text-slate-900 shadow-md ring-4 ring-white">
                    <Lock aria-hidden="true" className="size-4" />
                    <span className="sr-only">Nog geen abonnement</span>
                  </span>
                ) : null}
              </span>
              <span className="text-2xl font-bold">{child.firstName}</span>
              <span className="rounded-full bg-amber-100 px-3 py-0.5 font-bold text-amber-900">
                {copy.group(child.schoolGroup)}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </ChildShell>
  );
}

const names = new Intl.ListFormat('nl-NL', { style: 'long', type: 'conjunction' });

function SchoolYearBanner({ kids }: { readonly kids: readonly FamilyChild[] }) {
  if (kids.length === 0) {
    return null;
  }
  return (
    <Link
      to="/ouders"
      className="group mx-auto mt-8 flex max-w-2xl items-center gap-4 overflow-hidden rounded-4xl bg-white py-3 pr-4 pl-3 shadow-warm ring-1 ring-amber-200 transition duration-300 hover:-translate-y-0.5 hover:shadow-raised sm:gap-5 sm:pr-6"
    >
      <span className="relative flex size-20 shrink-0 items-center justify-center rounded-3xl bg-linear-to-b from-amber-200 to-amber-300 sm:size-24">
        <Mascot
          pose="diploma"
          size="fit"
          className="h-[76%] transition duration-300 group-hover:-rotate-3"
        />
      </span>
      <span className="min-w-0 flex-1 text-left">
        <span className="block font-rounded text-xl leading-tight font-semibold text-slate-900 sm:text-2xl">
          {copy.newSchoolYearTitle}
        </span>
        <span className="mt-1 block text-base leading-snug text-pretty text-slate-600">
          {copy.newSchoolYearText(names.format(kids.map((child) => child.firstName)), kids.length)}
        </span>
      </span>
      <span className="hidden h-12 shrink-0 items-center gap-2 rounded-2xl bg-amber-400 px-4 text-base font-bold text-slate-900 shadow-[0_3px_0_0_var(--color-amber-600)] transition group-hover:bg-amber-300 sm:flex">
        {copy.newSchoolYearAction}
        <ArrowRight aria-hidden="true" className="size-5" />
      </span>
    </Link>
  );
}

function ParentsLink() {
  return (
    <Link
      to="/ouders"
      className="flex items-center gap-2 rounded-full bg-white px-4 py-2.5 text-base font-bold text-slate-700 shadow-warm ring-1 ring-slate-200 transition hover:bg-slate-50 hover:text-slate-900"
    >
      <UserRound aria-hidden="true" className="size-4" />
      {copy.parents}
    </Link>
  );
}
